import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Text,
  HStack,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DatePickerInput from '../common/DatePickerInput';

interface TrackingPeriodStepProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onNext: () => void;
  onBack: () => void;
}

const TrackingPeriodStep = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onNext,
  onBack,
}: TrackingPeriodStepProps) => {
  const [error, setError] = useState<string | null>(null);

  const handleNext = () => {
    // Validate dates
    if (startDate >= endDate) {
      setError('Start date must be before end date');
      return;
    }

    // At least 30 days between start and end
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      setError('Tracking period must be at least 30 days');
      return;
    }

    onNext();
  };

  return (
    <Box>
      <Text mb={6}>
        Select the start and end dates for your billable hours tracking period.
        This is typically a calendar year or your firm's fiscal year.
      </Text>

      <Stack spacing={6} mb={8}>
        <FormControl isInvalid={!!error}>
          <FormLabel>Start Date</FormLabel>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => date && onStartDateChange(date)}
            customInput={<DatePickerInput />}
            dateFormat="MM/dd/yyyy"
          />
          <FormHelperText>
            Select the first day of your tracking period
          </FormHelperText>
        </FormControl>

        <FormControl isInvalid={!!error}>
          <FormLabel>End Date</FormLabel>
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => date && onEndDateChange(date)}
            customInput={<DatePickerInput />}
            dateFormat="MM/dd/yyyy"
            minDate={new Date(startDate.getTime() + 86400000)} // startDate + 1 day
          />
          <FormHelperText>
            Select the last day of your tracking period
          </FormHelperText>
        </FormControl>

        {error && (
          <Text color="red.500" mt={2}>
            {error}
          </Text>
        )}
      </Stack>

      <HStack spacing={4} justifyContent="space-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button colorScheme="blue" onClick={handleNext}>
          Next
        </Button>
      </HStack>
    </Box>
  );
};

export default TrackingPeriodStep;